/**
 * PastInterviews Component - Interview History List
 * Displays past interview sessions with scores and details
 */
import React, { memo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Clock, Target, Award, ChevronRight, Calendar,
  TrendingUp, MessageSquare, Star, Users, Zap, Code, DollarSign
} from "lucide-react";
import { formatDate, getScoreColor, getScoreBgColor } from "./constants";

const PastInterviews = memo(({ 
  sessions = [], 
  onViewDetails, 
  loading = false 
}) => {
  const getModeIcon = (mode) => {
    switch (mode) {
      case "panel": return <Users className="w-4 h-4" />;
      case "stress": return <Zap className="w-4 h-4" />;
      case "technical": return <Code className="w-4 h-4" />;
      case "salary": return <DollarSign className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const handleViewDetails = useCallback((session) => {
    onViewDetails?.(session);
  }, [onViewDetails]);

  if (loading) {
    return (
      <Card className="border-0 shadow-lg bg-white/80">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-gray-600">Loading interview history...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (sessions.length === 0) {
    return (
      <Card className="border-0 shadow-lg bg-white/80">
        <CardContent className="py-12">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Past Interviews</h3>
            <p className="text-gray-600">
              Complete your first interview to see your history here.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Interview History ({sessions.length})
        </h3>
      </div>

      {sessions.map((session, idx) => {
        const overallScore = session.overall_score || 
          Math.round((
            (session.technical_score || 0) + 
            (session.communication_score || 0) + 
            (session.confidence_score || 0)
          ) / 3);

        return (
          <Card 
            key={session.id || idx} 
            className="border-0 shadow-md bg-white hover:shadow-lg transition-shadow cursor-pointer group"
            onClick={() => handleViewDetails(session)}
            data-testid={`past-interview-card-${idx}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                {/* Left: Session Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="font-semibold text-gray-800 truncate">
                      {session.target_role || session.career_field || "Interview Session"}
                    </h4>
                    <Badge variant="outline" className="flex items-center gap-1">
                      {getModeIcon(session.mode)}
                      <span className="capitalize">{session.mode || "standard"}</span>
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(session.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      {session.questions_answered || session.responses?.length || 0} questions
                    </span>
                    {session.industry && (
                      <span className="text-gray-400">
                        {session.industry}
                      </span>
                    )}
                  </div>

                  {/* Score Badges */}
                  <div className="flex flex-wrap gap-2">
                    {session.technical_score !== undefined && (
                      <Badge className={`${getScoreBgColor(session.technical_score)} ${getScoreColor(session.technical_score)}`}>
                        Technical: {session.technical_score}%
                      </Badge>
                    )}
                    {session.communication_score !== undefined && (
                      <Badge className={`${getScoreBgColor(session.communication_score)} ${getScoreColor(session.communication_score)}`}>
                        Communication: {session.communication_score}%
                      </Badge>
                    )}
                    {session.confidence_score !== undefined && (
                      <Badge className={`${getScoreBgColor(session.confidence_score)} ${getScoreColor(session.confidence_score)}`}>
                        Confidence: {session.confidence_score}%
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Right: Overall Score & Action */}
                <div className="flex flex-col items-end gap-2">
                  <div className={`w-16 h-16 rounded-xl flex flex-col items-center justify-center ${getScoreBgColor(overallScore)}`}>
                    <span className={`text-2xl font-bold ${getScoreColor(overallScore)}`}>
                      {overallScore}
                    </span>
                    <span className="text-xs text-gray-500">Score</span>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="group-hover:bg-blue-50 group-hover:text-blue-600"
                  >
                    View Details
                    <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
});

PastInterviews.displayName = "PastInterviews";

export default PastInterviews;
